import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Grid from '@material-ui/core/Grid';
import {
	Hidden,
	IconButton,
	List,
	ListItem,
	ListItemText,
	makeStyles,
	MenuItem,
	Select,
	SwipeableDrawer,
	Tooltip,
	Typography,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import i18next from 'i18next';

import { AppRoutePath } from 'router/Routes';
import logo from 'assets/logo.png';

import AppLink, { AppLinkProps } from '../link/AppLink';

enum Languages {
	CS = 'csCZ',
	EN = 'en',
	DE = 'de',
}

const appLinks: AppLinkProps[] = [
	{
		to: AppRoutePath.HOMEPAGE,
		i18nLabelKey: 'homepage',
	},
	{
		to: AppRoutePath.SEARCH,
		i18nLabelKey: 'search',
	},
	{
		to: AppRoutePath.HELP,
		i18nLabelKey: 'help',
	},
	{
		to: AppRoutePath.CONTACT,
		i18nLabelKey: 'contact',
	},
];

const useStyles = makeStyles(theme => ({
	logo: {
		marginRight: '35px',
	},
	divider: {
		flexGrow: 1,
	},
	contentContainer: {
		marginTop: theme.mixins.toolbar.maxHeight,
		paddingTop: '4px',
		paddingBottom: '32px',
	},
	paper: {
		top: '3px',
	},
}));

const Layout: FC = ({ children }) => {
	const classes = useStyles();

	const { t } = useTranslation('navigation');

	const [lang, setLang] = useState<Languages | undefined>();

	useEffect(() => {
		setLang(i18next.language as Languages);
	}, [i18next.language]);

	const [open, setOpen] = useState(false);

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleLang = (event: React.ChangeEvent<{ value: unknown }>) => {
		i18next.changeLanguage(event.target.value as string);
	};

	return (
		<>
			<AppBar>
				<Toolbar>
					<Hidden only={['xs']}>
						<img src={logo} alt="Logo" className={classes.logo} />
					</Hidden>

					<Typography variant="h6">{t('page_title')}</Typography>

					<span className={classes.divider} />

					<Hidden only={['lg', 'xl', 'md']}>
						<Tooltip title={t('menu_icon_tooltip')}>
							<IconButton onClick={open ? handleClose : handleOpen}>
								<MenuIcon />
							</IconButton>
						</Tooltip>

						<SwipeableDrawer
							classes={{ paper: classes.paper }}
							anchor="right"
							open={open}
							onClose={handleClose}
							onOpen={handleOpen}
						>
							<nav>
								<List>
									{appLinks.map((link, index) => (
										<ListItem key={`nav-item:${index}`}>
											<ListItemText>
												<AppLink
													key={`nav-link:${index}`}
													to={link.to}
													i18nLabelKey={link.i18nLabelKey}
													onClick={handleClose}
												/>
											</ListItemText>
										</ListItem>
									))}
								</List>
							</nav>
							<Select
								style={{ paddingLeft: '15px', paddingRight: '15px' }}
								value={lang}
								placeholder={lang?.toUpperCase()}
								onChange={handleLang}
							>
								<MenuItem value={Languages.CS}>CZ</MenuItem>
								<MenuItem value={Languages.EN}>EN</MenuItem>
								<MenuItem value={Languages.DE}>DE</MenuItem>
							</Select>
						</SwipeableDrawer>
					</Hidden>
					<Hidden only={['xs', 'sm']}>
						<nav>
							{appLinks.map((link, index) => (
								<AppLink
									key={`nav-link:${index}`}
									to={link.to}
									i18nLabelKey={link.i18nLabelKey}
								/>
							))}
						</nav>
						<Select
							value={lang}
							placeholder={lang?.toUpperCase()}
							onChange={handleLang}
						>
							<MenuItem value={Languages.CS}>CZ</MenuItem>
							<MenuItem value={Languages.EN}>EN</MenuItem>
							<MenuItem value={Languages.DE}>DE</MenuItem>
						</Select>
					</Hidden>
				</Toolbar>
			</AppBar>

			<Grid
				className={classes.contentContainer}
				container
				direction="row"
				justify="center"
				alignItems="center"
			>
				{children}
			</Grid>
		</>
	);
};

export default Layout;
